import {
  abacPoliciesSelector,
  ABACPolicyEntity,
  firstOrUndefined,
  PermissionEntity,
  useGetABACPoliciesByPermissionQuery
} from "@ots-frontend/ots-data";
import {ActionIcon, Card, Divider, Grid, Group, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {useCallback, useMemo} from "react";
import {PAGINATION_PAGE_SIZES} from "../../data-table/DataTable/usePagination";
import {IconPlus} from "@tabler/icons-react";
import {BaseABACPolicyDataTable} from "../../data-table/ABACPolicyDataTable/BaseABACPolicyDataTable";
import {useDisclosure} from "@mantine/hooks";
import { EntityActionGroup } from "../../group/EntityActionGroup/EntityActionGroup";
import { usePermissionMutation } from "../../../hooks/usePermissionMutation";
import { ABACPolicySelectModal } from "../../select/entity/ABACPolicySelect/ABACPolicySelect";

export type PermissionDetailsProps = {
  permission: PermissionEntity
}

export const PermissionDetails = (props: PermissionDetailsProps) => {
  const {permission} = props;

  const {t} = useTranslation(["common"]);

  const [abacPolicySelectOpened, abacPolicySelectHandler] = useDisclosure(false);

  const selectABACPolicies = useMemo(() => {
    return abacPoliciesSelector();
  }, []);

  const {abacPolicies, isABACPoliciesLoading} = useGetABACPoliciesByPermissionQuery(permission.id, {
    selectFromResult: (result) => ({
      abacPolicies: selectABACPolicies(result),
      isABACPoliciesLoading: result.isLoading
    })
  });

  const {
    addABACPolicy,
    removeABACPolicy,
    isUpdating: isUpdatingABACPolicy,
  } = usePermissionMutation()

  const onABACPolicyAddClick = useCallback((abacPolicies: ABACPolicyEntity[] | undefined) => {
    const abacPolicy = firstOrUndefined(abacPolicies)
    if (abacPolicy) {
      addABACPolicy(permission.id, abacPolicy.id, () => abacPolicySelectHandler.close())
    }
  }, [addABACPolicy, permission.id, abacPolicySelectHandler])

  const renderActions = useCallback((record: ABACPolicyEntity, index: number) => {
    return (
      <EntityActionGroup
        permissionPrefix={"permission"}
        deletePermission={"edit"}
        view={false}
        edit={false}
        onDeleteActionClick={(e) => removeABACPolicy(permission.id, record.id, undefined)}
      />
    )
  }, [removeABACPolicy, permission.id])

  return (
    <Grid>
      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{
              t("common.entity.permission.profile.info.section.details.label", "Details")}
            </Title>
          </Group>

          <Divider/>

          <Grid>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.permission.profile.info.field.name.label", "Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {permission.name}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.permission.profile.info.field.description.label", "Description")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {permission.description}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>
      <Grid.Col span={12}>
        <HasAccess permissions={["permission_view"]}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>

            <Group position="apart" pb="sm">
              <Title order={4}>{
                t("common.entity.permission.profile.info.section.policies.label", "Policies")}
              </Title>
              <Group spacing={4} position="left" noWrap>
                <HasAccess permissions={["permission_edit"]}>
                  <ActionIcon color="green" onClick={() => abacPolicySelectHandler.open()}>
                    <IconPlus size={16}/>
                  </ActionIcon>
                </HasAccess>
              </Group>
            </Group>

            <Divider my="md"/>

            <BaseABACPolicyDataTable
              withBorder
              records={abacPolicies}
              recordsPerPageOptions={PAGINATION_PAGE_SIZES}
              fetching={isABACPoliciesLoading || isUpdatingABACPolicy}
              renderActions={renderActions}
            />
          </Card>
        </HasAccess>
      </Grid.Col>

      <ABACPolicySelectModal
        opened={abacPolicySelectOpened}
        onClose={abacPolicySelectHandler.close}
        title={t("common.entity.abacpolicy.plural", "ABAC Policies")}
        onSubmit={onABACPolicyAddClick}
      />
    </Grid>
  );
};
